import { InjectionToken } from '@angular/core';
import * as AuthSelectors from '@app/core/auth2/store/auth.selectors';
import * as fromLocalRouter from '@app/core/router';
import { RouterStateUrl } from '@app/core/router';
import { Go } from '@app/core/store/actions/router.actions';
import * as fromLogger from '@app/core/store/reducers/logger.reducer';
import * as fromMedia from '@app/core/store/reducers/media.reducer';
import { SettingsSelectors } from '@app/main/settings/store';
import * as fromTruckLiveData from '@app/main/trucks/store/truck-live-data.reducer';
import * as fromView from '@app/store/view.reducer';
import {
  AccountingUnit,
  Assignment,
  AssignmentStatus,
  AssignmentTarget,
  AssignmentTargetType,
  AssignmentType,
  BatchReport,
  BillableItem,
  BillableItemAssociation,
  CognitoUser,
  Company,
  Contact,
  Country,
  Customer,
  DeliverySlip,
  Employee,
  ExposureClass,
  Invoice,
  InvoiceTerm,
  IotGateway,
  Job,
  JobRelatedEntitiesAndStats,
  LiveBatch,
  MixDesign,
  MixDesignIngredient,
  MixDesignIngredientType,
  ObjectReference,
  personNameSelector,
  PriceLevel,
  Project,
  ProjectStats,
  Province,
  Role,
  Tax,
  TaxGroup,
  Truck,
  Unit,
} from '@cco/model';
import * as fromAuth from '@core/auth/reducers';
import * as fromAuthStatus from '@core/auth/reducers/auth';
import * as fromLoginPage from '@core/auth/reducers/login-page';
import { Logout } from '@core/auth2/store/auth2.actions';
import * as fromAuth2 from '@core/auth2/store/auth2.reducer';
import { environment } from '@env/environment';
import { EntitySelectors, EntitySelectorsFactory } from '@ngrx/data';
import { Dictionary } from '@ngrx/entity/src/models';
import * as fromRouter from '@ngrx/router-store';
import { RouterReducerState } from '@ngrx/router-store';
import {
  Action,
  ActionReducer,
  ActionReducerMap,
  combineReducers,
  createFeatureSelector,
  createSelector,
  MetaReducer,
} from '@ngrx/store';
import { DateTime } from 'luxon';
import { CcoEntityCollection } from './entity-ref';
import * as fromGlobalSelect from './global-select.reducer';
import * as fromGlobalFilters from './global-filter.reducer';
import * as fromTrackedRequests from './tracked-requests.reducer';
import { BillableItemAssociationType } from '@cco/model/lib/pricing/billable-item-association-type';
import { entityConfig } from '@app/store/entity-metadata';
import { CcoEntityMetadata } from '@app/data/entity-metadata';

export interface State {
  auth2: fromAuth2.State;
  selects: fromGlobalSelect.State;
  truckLiveData: fromTruckLiveData.State;
  view: fromView.State;
  router: fromRouter.RouterReducerState<RouterStateUrl>;
  auth: fromAuth.AuthState;
  logger: fromLogger.State;
  [fromTrackedRequests.featureKey]: fromTrackedRequests.State;
  globalFilters: fromGlobalFilters.State;
}

export const reducers: ActionReducerMap<State> = {
  auth2: fromAuth2.reducer,
  selects: fromGlobalSelect.reducer,
  truckLiveData: fromTruckLiveData.reducer,
  view: combineReducers(fromView.reducers),
  router: fromRouter.routerReducer,
  auth: combineReducers({
    status: fromAuthStatus.reducer,
    loginPage: fromLoginPage.reducer,
  }),
  logger: fromLogger.reducer,
  [fromTrackedRequests.featureKey]: fromTrackedRequests.reducer,
  globalFilters: fromGlobalFilters.reducer,
};

export const REDUCER_TOKEN = new InjectionToken<ActionReducerMap<State>>('Root Reducer');

export function getReducers() {
  return reducers;
}

export function clearState(reducer: ActionReducer<State>): ActionReducer<State> {
  return (state: State, action: Action): State => {
    if (action.type === Logout.type) {
      return reducer(
        {
          auth2: fromAuth2.initialState,
          selects: fromGlobalSelect.initialState,
          truckLiveData: fromTruckLiveData.initialState,
          view: fromView.initialState,
          router: state.router, // Is there an initial state we can use for this?
          auth: fromAuth.initialState,
          logger: fromLogger.initialState,
          [fromTrackedRequests.featureKey]: fromTrackedRequests.initialState,
          globalFilters: fromGlobalFilters.initialState,
        },
        action,
      );
    }

    return reducer(state, action);
  };
}

/*
export function localStorageSyncReducer(reducer: ActionReducer<State>): ActionReducer<State> {
  return selectedSync({
    keys: ['auth2', 'entityCache', 'iotThings', 'selects', 'settings', 'auth', 'logger'],
    restoreDates: false,
    rehydrate: true,
    removeOnUndefined: true,
  })(reducer);
}
*/

export const metaReducers: MetaReducer<State>[] = !environment.production
  ? [clearState]
  : [clearState];

export function actionSanitizer(action: Action): Action {
  /*
  if (action.type === GetAssignmentStatusesSuccess.type) {
    const a = action as any;

    if (a.payload && a.payload.assignmentStatuses) {
      const assignmentStatuses: AssignmentStatus[] = [];
      a.payload.assignmentStatuses.forEach((status) => {
        const validTransitions: ValidAssignmentStatusTransition[] = [];

        status.validTransitions.forEach((transition) =>
          validTransitions.push({ ...transition, description: '...' }),
        );
        assignmentStatuses.push({ ...status, description: '...', validTransitions });
      });

      const newAction = GetAssignmentStatusesSuccess({ assignmentStatuses });
      // console.log('newAction', newAction, 'type', action.type);
      // console.log('id', id);
      return newAction;
    }
  }

  if (action.type === AddDeliverySlip.type) {
    const a = action as any;

    if (a.payload && a.payload.deliverySlip && a.payload.deliverySlip.receiverSignature) {
      const newAction = AddDeliverySlip({
        deliverySlip: {
          ...a.payload.deliverySlip,
          receiverSignature: a.payload.deliverySlip.receiverSignature ? '...' : null,
        },
      });
      return newAction;
    }
  }

  if (action.type === GetDeliverySlipsSuccess.type) {
    const a = action as any;

    if (a.payload && a.payload.deliverySlips) {
      const redactedDeliverySlips: DeliverySlip[] = [];

      a.payload.deliverySlips.forEach((deliverySlip) => {
        redactedDeliverySlips.push({
          ...deliverySlip,
          receiverSignature: deliverySlip.receiverSignature ? '...' : null,
        });
      });

      return GetDeliverySlipsSuccess({ deliverySlips: redactedDeliverySlips });
    }
  }
  */

  if (action.type === Go.type) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const a = action as any;

    return Go({ path: a.path });
  }

  // console.log('action', action, 'type', action.type);
  // console.log('id', id);
  return action;
}

export const entitySelectorFactory = new EntitySelectorsFactory();

export const accountingUnitSelectors = entitySelectorFactory.create<AccountingUnit>('AccountingUnit');
export const assignmentSelectors = entitySelectorFactory.create<Assignment>('Assignment');
export const assignmentStatusSelectors = entitySelectorFactory.create<AssignmentStatus>(
  'AssignmentStatus',
);
export const assignmentTargetSelectors = entitySelectorFactory.create<AssignmentTarget>(
  'AssignmentTarget',
);
export const assignmentTargetTypeSelectors = entitySelectorFactory.create<AssignmentTargetType>(
  'AssignmentTargetType',
);
export const assignmentTypeSelectors = entitySelectorFactory.create<AssignmentType>(
  'AssignmentType',
);
export const batchReportSelectors = entitySelectorFactory.create<BatchReport>('BatchReport');
export const companySelectors = entitySelectorFactory.create<Company>('Company');
export const contactSelectors = entitySelectorFactory.create<Contact>('Contact');
export const countrySelectors = entitySelectorFactory.create<Country>('Country');
export const customerSelectors = entitySelectorFactory.create<Customer>('Customer');
export const deliverySlipSelectors = entitySelectorFactory.create<DeliverySlip>('DeliverySlip');
export const billableItemSelectors = entitySelectorFactory.create<BillableItem>('BillableItem');
export const billableItemAssociationSelectors = entitySelectorFactory.create<BillableItemAssociation>('BillableItemAssociation');
export const billableItemAssociationTypeSelectors = entitySelectorFactory.create<BillableItemAssociationType>('BillableItemAssociationType');
export const employeeSelectors = entitySelectorFactory.create<Employee>('Employee');
export const exposureClassSelectors = entitySelectorFactory.create<ExposureClass>('ExposureClass');
export const invoiceSelectors = entitySelectorFactory.create<Invoice>('Invoice');
export const iotGatewaySelectors = entitySelectorFactory.create<IotGateway>('IotGateway');
export const jobSelectors = entitySelectorFactory.create<Job>('Job');
export const jobRelatedEntitiesAndStatsSelectors = entitySelectorFactory.create<JobRelatedEntitiesAndStats>('JobRelatedEntitiesAndStats');
export const liveBatchSelectors = entitySelectorFactory.create<LiveBatch>('LiveBatch');
export const mixDesignSelectors = entitySelectorFactory.create<MixDesign>('MixDesign');
export const mixDesignIngredientSelectors = entitySelectorFactory.create<MixDesignIngredient>(
  'MixDesignIngredient',
);
export const mixDesignIngredientTypeSelectors = entitySelectorFactory.create<MixDesignIngredientType>(
  'MixDesignIngredientType',
);
export const priceLevelSelectors = entitySelectorFactory.create<PriceLevel>('PriceLevel');
export const projectSelectors = entitySelectorFactory.create<Project>('Project');
export const projectStatsSelectors = entitySelectorFactory.create<ProjectStats>('ProjectStats');
export const provinceSelectors = entitySelectorFactory.create<Province>('Province');
export const roleSelectors = entitySelectorFactory.create<Role>('Role');
export const taxSelectors = entitySelectorFactory.create<Tax>('Tax');
export const taxGroupSelectors = entitySelectorFactory.create<TaxGroup>('TaxGroup');
export const truckSelectors = entitySelectorFactory.create<Truck>('Truck');
export const unitSelectors = entitySelectorFactory.create<Unit>('Unit');
export const userSelectors = entitySelectorFactory.create<CognitoUser>('User');

const entitySelectors: { [entityName: string]: EntitySelectors<any> } = {
};

export const selectEntitySelectors = (entityName: string) => {
  if (entitySelectors[entityName] == null)
    entitySelectors[entityName] = entitySelectorFactory.create(entityName);

  return entitySelectors[entityName];
};

export const selectObjectByRef = <T> (objectRef: ObjectReference) => {
  return objectRef?.objectType == null
    ? createSelector(() => null)
    : createSelector(selectEntitySelectors(objectRef.objectType).selectEntityMap, entityMap => entityMap?.[objectRef.objectId] as T);
};

export const selectGlobalFiltersState = createFeatureSelector<fromGlobalFilters.State>('globalFilters');

export const selectSelectsState = createFeatureSelector<fromGlobalSelect.State>('selects');
export const selectSelectedEntityId = (entityName: string) =>
  createSelector(selectSelectsState, state => state[entityName]);
export const selectSelectedEntity = <T>(entityName: string) =>
  createSelector(
    selectSelectedEntityId(entityName),
    entitySelectorFactory.create<T>(entityName).selectEntityMap,
    (selectedId, entityMap) => {
      return entityMap && entityMap[selectedId];
    },
  );

export const selectEntityByKey = <T>(entityName: string) =>
  createSelector(
    entitySelectorFactory.create<T>(entityName).selectEntityMap,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (entityMap: Dictionary<T>, key: any) => {
      return entityMap?.[key];
    },
  );

export const selectLoadedAt = <T>(entityName: string) =>
  createSelector(
    entitySelectorFactory.create<T>(entityName).selectCollection,
    ec => (ec as CcoEntityCollection<T>).loadedAt
  );

export const selectQueryLoadedAt = <T>(entityName: string) =>
  createSelector(
    entitySelectorFactory.create<T>(entityName).selectCollection,
    ec => (ec as CcoEntityCollection<T>).queryLoadedAt,
  );

export const selectDataLoadedWithGlobalFilter = <T>(entityName: string) =>
  createSelector(
    selectGlobalFilterQuery(entityName),
    entitySelectorFactory.create<T>(entityName).selectCollection,
    (query, ec) => {
      const entityCollection = ec as CcoEntityCollection<T>;
      if (query) {
        const filteredQuery = Object.fromEntries(Object.entries(query).filter(([, value]) => value != null));

        const allPossibleSortedKeys = Object.keys(filteredQuery)
          .reduce((permutations: any[]) =>
            permutations.flatMap((permutation: any) =>
              permutation.length === Object.keys(filteredQuery).length
                ? [permutation]
                : Object.keys(filteredQuery).filter(k => !permutation.includes(k)).map(k => [...permutation, k])
            ), [[]])
          .map((keysInOrder) => {
            const obj = {};
            for (const key of keysInOrder) {
              obj[key] = filteredQuery[key];
            }
            return JSON.stringify(obj);
          });

        for (const key of allPossibleSortedKeys) {
          if (entityCollection?.queryLoadedAt?.[key] != null) {
            return entityCollection.queryLoadedAt[key];
          }
        }
      }
      return entityCollection.loadedAt;
    }
  );

const timeBasedEntityNames = Object.keys(entityConfig.entityMetadata)
  .filter(k => (entityConfig.entityMetadata[k] as CcoEntityMetadata).timeBased);

export const selectGlobalFilterQuery = (entityName: string) => createSelector(
  selectGlobalFiltersState,
  (globalFilters) => {

    return timeBasedEntityNames.includes(entityName) ? globalFilters : null;
  }
);

export const selectSelectedAssignmentId = selectSelectedEntityId('Assignment');

export const selectYards = createSelector(
  assignmentTargetSelectors.selectEntities,
  assignmentTargetTypeSelectors.selectEntities,
  (targets, types) => {
    if (!targets || !types) {
      return null;
    }

    const yardType = types.find(type => type.name === 'Yard');
    if (!yardType) {
      return null;
    }
    return targets.filter(target => target.typeId === yardType.id);
  },
);
export const selectSelectedAssignmentTargetName = createSelector(
  selectSelectedEntity<AssignmentTarget>('AssignmentTarget'),
  assignmentTarget => assignmentTarget?.name ?? 'New',
);

export const selectAuth2State = createFeatureSelector<fromAuth2.State>('auth2');
export const selectCurrentUser = createSelector(selectAuth2State, state => state.currentUser);
export const selectCurrentUserId = createSelector(selectCurrentUser, user => user.tenantUser?.id);
export const selectCurrentUserPrivileges = createSelector(
  selectAuth2State,
  fromAuth2.selectCurrentUserPrivileges,
);
export const selectAuth2StateState = createSelector(selectAuth2State, fromAuth2.selectState);
export const selectAuth2StatePreviousState = createSelector(
  selectAuth2State,
  fromAuth2.selectPreviousState,
);
export const selectAuth2RedirectUrl = createSelector(selectAuth2State, fromAuth2.selectRedirectUrl);

export const selectCurrentUserHasPrivilege = (requiredPrivilege: string) => createSelector(
  selectCurrentUserPrivileges,
  (currentUserPrivileges: string[]) => {
    if (!currentUserPrivileges) {
      return false;
    }

    return currentUserPrivileges.includes(requiredPrivilege);
  },
);

// TODO: Fix me!
export const selectBatchReportIdsByJobIds = createSelector(
  batchReportSelectors.selectCollection,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  collection => [],
);
export const selectBatchReportsWithoutDeliverySlips = createSelector(
  batchReportSelectors.selectEntities,
  (batchReports) => {
    if (!batchReports) {
      return null;
    }

    return batchReports.filter(batchReport => batchReport.deliverySlipId == null);
  },
);
export const selectSelectedBatchReport = selectSelectedEntity<BatchReport>('BatchReport');
export const selectSelectedBatchReportId = createSelector(
  selectSelectsState,
  state => state.BatchReport,
);
export const selectSelectedBatchReportIdAsArgs = createSelector(
  selectSelectedBatchReport,
  (selectedBatchReport) => {
    if (!selectedBatchReport || !selectedBatchReport.id) {
      return null;
    }

    return { batchReportId: selectedBatchReport.id };
  },
);

export const selectContactById = createSelector(
  contactSelectors.selectEntityMap,
  (entityMap, id: number) => entityMap && entityMap[id],
);
export const selectSelectedContact = selectSelectedEntity<Contact>('Contact');
export const selectSelectedContactName = createSelector(
  selectSelectedContact,
  contact => (contact && contact.givenNames + ' ' + contact.surname) || 'New',
);

export const selectCountryById = createSelector(
  countrySelectors.selectEntityMap,
  (countries: Dictionary<Country>, id: number) => countries && countries[id],
);

export const selectCustomerById = createSelector(
  customerSelectors.selectEntityMap,
  (customers: Dictionary<Customer>, id: number) => customers && customers[id],
);
export const selectSelectedCustomerId = createSelector(
  selectSelectsState,
  state => state?.Customer,
);
export const selectSelectedCustomer = createSelector(
  customerSelectors.selectEntityMap,
  selectSelectedCustomerId,
  (entityMap, selectedId) => entityMap && entityMap[selectedId],
);
export const selectSelectedCustomerName = createSelector(
  selectSelectedCustomer,
  customer => customer?.name || 'New',
);

export const selectDeliverySlipById = (id: number) => createSelector(
  deliverySlipSelectors.selectEntityMap,
  entityMap => entityMap?.[id],
);
export const selectSelectedDeliverySlip = selectSelectedEntity<DeliverySlip>('DeliverySlip');
export const selectSelectedDeliverySlipUuid = createSelector(
  selectSelectedDeliverySlip,
  item => item?.uuid || 'New',
);
export const selectSelectedDeliverySlipDocumentNumber = createSelector(
  selectSelectedDeliverySlip,
  deliverySlip => deliverySlip?.documentNumber ?? 'New',
);

export const selectBillableItemById = (id: number) => createSelector(
  billableItemSelectors.selectEntityMap,
  entityMap => entityMap?.[id]);
export const selectSelectedBillableItem = selectSelectedEntity<BillableItem>('BillableItem');
export const selectSelectedBillableItemSku = createSelector(
  selectSelectedBillableItem,
  item => item?.sku ?? 'New',
);

export const selectEmployeesWithTenantUser = createSelector(
  employeeSelectors.selectEntities,
  (employees: Employee[]) => employees.filter(employee => employee.tenantUserId != null)
);
export const selectEmployeeById = createSelector(
  employeeSelectors.selectEntityMap,
  (employees: Dictionary<Employee>, id: number) => {
    return (employees && employees[id]) || null;
  },
);
export const selectEmployeeByUserSubject = createSelector(
  employeeSelectors.selectEntities,
  (employees: Employee[], sub: string) => {
    if (!employees) {
      return null;
    }
    return employees.find(employee => employee.userSubject === sub);
  },
);
export const selectSelectedEmployee = selectSelectedEntity<Employee>('Employee');
export const selectSelectedEmployeeName = createSelector(
  selectSelectedEmployee,
  personNameSelector,
);
export const selectSelectedEmployeeId = createSelector(selectSelectsState, state => state.Employee);

export const selectIotGatewayById = createSelector(
  iotGatewaySelectors.selectEntityMap,
  (iotGateways: Dictionary<IotGateway>, id: number) => iotGateways && iotGateways[id],
);
export const selectSelectedIotGateway = selectSelectedEntity<IotGateway>('IotGateway');
export const selectSelectedIotGatewayName = createSelector(
  selectSelectedIotGateway,
  gateway => gateway?.thingName,
);
export const selectSelectedIotGatewayId = createSelector(
  selectSelectsState,
  state => state.IotGateway,
);

export const selectMixDesignById = createSelector(
  mixDesignSelectors.selectEntityMap,
  (mixDesigns: Dictionary<MixDesign>, id: number) => mixDesigns && mixDesigns[id],
);
export const selectSelectedMixDesign = selectSelectedEntity<MixDesign>('MixDesign');
export const selectSelectedMixDesignId = createSelector(
  selectSelectsState,
  state => state.MixDesign,
);
export const selectSelectedMixDesignName = createSelector(
  selectSelectedMixDesign,
  mixDesign => mixDesign?.name ?? 'New',
);

export const selectMixDesignIngredientTypeById = createSelector(
  mixDesignIngredientTypeSelectors.selectEntityMap,
  (mixDesignIngredientTypes: Dictionary<MixDesignIngredientType>, id: number) =>
    mixDesignIngredientTypes && mixDesignIngredientTypes[id],
);

export const selectMixDesignIngredientById = createSelector(
  mixDesignIngredientSelectors.selectEntityMap,
  (mixDesignIngredients: Dictionary<MixDesignIngredient>, id: number) =>
    mixDesignIngredients && mixDesignIngredients[id],
);
export const selectSelectedMixDesignIngredient = selectSelectedEntity<MixDesignIngredient>(
  'MixDesignIngredient',
);
export const selectSelectedMixDesignIngredientName = createSelector(
  selectSelectedMixDesignIngredient,
  ingredient => ingredient?.name ?? 'New',
);

export const selectSelectedInvoiceTerm = selectSelectedEntity<InvoiceTerm>('InvoiceTerm');
export const selectSelectedInvoiceTermName = createSelector(
  selectSelectedInvoiceTerm,
  term => term?.name ?? 'New',
);

export const selectSelectedInvoice = selectSelectedEntity<Invoice>('Invoice');
export const selectSelectedInvoiceNumber = createSelector(
  selectSelectedInvoice,
  invoice => invoice?.invoiceNumber ?? 'New',
);

export const selectJobById = createSelector(
  jobSelectors.selectEntityMap,
  (jobs: Dictionary<Job>, id: number) => jobs && jobs[id],
);
export const selectJobsByProjectId = createSelector(
  jobSelectors.selectEntities,
  (jobs: Job[], projectId: number) => jobs?.filter(job => job.projectId === projectId),
);
export const selectUpcomingJobs = createSelector(jobSelectors.selectEntities, (jobs: Job[]) => {
  const now = DateTime.local();
  return jobs?.filter((job) => {
    if (job && job.scheduledInterval && job.scheduledInterval.start) {
      const start = DateTime.fromISO(job.scheduledInterval.start);
      if (start.isValid) {
        return start > now;
      }
    }

    return false;
  });
});
export const selectSelectedJob = selectSelectedEntity<Job>('Job');
export const selectSelectedJobId = createSelector(selectSelectsState, state => state.Job);
export const selectSelectedJobName = createSelector(selectSelectedJob, job => job?.name ?? 'New');

export const selectPriceLevelById = createSelector(
  priceLevelSelectors.selectEntityMap,
  (priceLevels: Dictionary<PriceLevel>, id: number) => priceLevels && priceLevels[id],
);
export const selectSelectedPriceLevel = selectSelectedEntity<PriceLevel>('PriceLevel');
export const selectSelectedPriceLevelName = createSelector(
  selectSelectedPriceLevel,
  priceLevel => priceLevel?.name ?? 'New',
);
export const selectSelectedPriceLevelId = createSelector(
  selectSelectsState,
  state => state.PriceLevel,
);
export const selectProjectsByInterval = (props: {start: DateTime; end: DateTime }) => createSelector(
  projectSelectors.selectEntities,
  (projects: Project[]) => {
    const projectsWithinDay: Project[] = [];
    for (const project of projects) {
      if (props.start <= DateTime.fromISO(project.interval?.end) &&
        props.end >= DateTime.fromISO(project.interval?.start)
      ) {
        projectsWithinDay.push(project);
      }
    }
    return projectsWithinDay;
  }
);
export const selectProjectById = createSelector(
  projectSelectors.selectEntityMap,
  (projects: Dictionary<Project>, props: { projectId: number }) =>
    projects && projects[props.projectId],
);
export const selectSelectedProject = selectSelectedEntity<Project>('Project');
export const selectSelectedProjectName = createSelector(
  selectSelectedProject,
  project => project?.name ?? 'New',
);
export const selectSelectedProjectId = createSelector(selectSelectsState, state => state?.Project);

export const selectSelectedProjectStats = createSelector(
  selectSelectedEntityId('Project'),
  projectStatsSelectors.selectEntityMap,
  (selectedProjectId, projectEntityMap) => projectEntityMap?.[selectedProjectId],
);

export const selectProvinceById = createSelector(
  provinceSelectors.selectEntityMap,
  (provinces: Dictionary<Province>, id: number) => provinces && provinces[id],
);
export const selectProvincesByCountryId = (countryId: number) => createSelector(
  provinceSelectors.selectEntities,
  (provinces: Province[]) =>
    provinces?.filter(province => province.countryId === countryId),
);

export const selectRouterReducerState = createFeatureSelector<RouterReducerState<RouterStateUrl>>(
  'router',
);
export const selectRouterState = createSelector(selectRouterReducerState, state => state.state);
export const selectRouterUrl = createSelector(selectRouterState, state => state.url);
export const selectRouterQueryParams = createSelector(
  selectRouterState,
  state => state.queryParams,
);
export const selectRouterFlatParams = createSelector(selectRouterState, state => state.flatParams);
export const selectRouterRouteParams = createSelector(
  selectRouterState,
  fromLocalRouter.selectRouteParams,
);
export const selectRouterRouteParamsForDepth = (depth: number) =>
  createSelector(selectRouterRouteParams, fromLocalRouter.selectParamsForDepth(depth));
export const selectRouteParam = (paramName: string) => createSelector(
  selectRouterState,
  (state: RouterStateUrl) => {
    for (let i = state.routeParams.length - 1; i >= 0; i--) {
      if (state.routeParams[i][paramName]) {
        return state.routeParams[i][paramName] as string;
      }
    }
  },
);
export const selectRouteParamAsNumber = (paramName: string) => createSelector(
  selectRouteParam(paramName),
  (param: string) => param == null ? param : +param
);
export const selectRouteParamAsBoolean = (paramName: string) => createSelector(
  selectRouteParam(paramName),
  (param: string) => param == null ? param : param === 'true'
);
export const selectRouterRouteData = createSelector(
  selectRouterState,
  fromLocalRouter.selectRouteData,
);
export const selectRouterRouteDataForDepth = (depth: number) =>
  createSelector(selectRouterRouteData, fromLocalRouter.selectDataForDepth(depth));
export const selectRouterParamNodes = createSelector(
  selectRouterState,
  fromLocalRouter.selectParamNodes,
);
export const selectRouterParamNodesForKey = (key: string) =>
  createSelector(selectRouterParamNodes, fromLocalRouter.selectParamNodesForKey(key));
export const selectRouterParamValueForKeyAndDepth = (key: string, depth: number) =>
  createSelector(
    selectRouterParamNodes,
    fromLocalRouter.selectParamValueForKeyAndDepth(key, depth),
  );
export const selectRouterActivatedRouteContainsMobilePath = createSelector(
  selectRouterRouteData,
  (routeData) => {
    if (!routeData) {
      return false;
    }

    return routeData.findIndex(routeDatum => routeDatum.mobilePath != null) !== -1;
  },
);
export const selectRouterActivatedRouteContainsFullPath = createSelector(
  selectRouterRouteData,
  (routeData) => {
    if (!routeData) {
      return false;
    }

    return routeData.findIndex(routeDatum => routeDatum.fullPath != null) !== -1;
  },
);

export const selectTaxById = createSelector(
  taxSelectors.selectEntityMap,
  (entityMap: Dictionary<Tax>, id: number) => entityMap && entityMap[id],
);
export const selectSelectedTax = selectSelectedEntity<Tax>('Tax');
export const selectSelectedTaxName = createSelector(selectSelectedTax, tax => tax?.code ?? 'New');
export const selectSelectedTaxId = createSelector(selectSelectsState, state => state.Tax);

export const selectSelectedTaxGroup = selectSelectedEntity<TaxGroup>('TaxGroup');
export const selectSelectedTaxGroupName = createSelector(
  selectSelectedTaxGroup,
  taxGroup => taxGroup?.code ?? 'New',
);

export const selectTruckById = createSelector(
  truckSelectors.selectEntityMap,
  (trucks: Dictionary<Truck>, id: number) => trucks && trucks[id],
);
export const selectSelectedTruckId = createSelector(selectSelectsState, state => state?.Truck);
export const selectSelectedTruck = selectSelectedEntity<Truck>('Truck');
export const selectSelectedTruckName = createSelector(selectSelectedTruck, truck => truck?.name ?? 'New');
export const selectSelectedTruckUuid = createSelector(selectSelectedTruck, truck => truck?.uuid);

export const selectTruckLiveDataState = createFeatureSelector<fromTruckLiveData.State>(
  'truckLiveData',
);
export const selectTruckLatestGpsData = createSelector(
  selectTruckLiveDataState,
  fromTruckLiveData.selectLatestGpsData,
);
export const selectIsLatestGpsDataSubscribedForTruck = createSelector(
  selectTruckLiveDataState,
  fromTruckLiveData.selectIsLatestGpsDataSubscribedForTruck,
);
export const selectLatestGpsDataForTruck = createSelector(
  selectTruckLiveDataState,
  fromTruckLiveData.selectLatestGpsDataForTruck,
);

export const selectRoleById = createSelector(
  roleSelectors.selectEntityMap,
  (entityMap: Dictionary<Role>, id: string) => entityMap && entityMap[id],
);

export const selectSelectedRoleId = createSelector(selectSelectsState, state => state.Role);

export const selectUserById = createSelector(
  userSelectors.selectEntityMap,
  (entityMap: Dictionary<CognitoUser>, id: string) => entityMap && entityMap[id],
);

export const selectSelectedUnit = selectSelectedEntity<Unit>('Unit');
export const selectSelectedUnitShortName = createSelector(selectSelectedUnit, unit => unit?.shortName);

export const selectSelectedUserId = createSelector(selectSelectsState, state => state.User);
export const selectSelectedUser = selectSelectedEntity<CognitoUser>('User');
export const selectSelectedUserName = createSelector(
  selectSelectedUser,
  user => user?.givenNames && user?.surname ? user?.givenNames + ' ' + user?.surname[0] + '.' : 'New',
);

export const selectViewState = createFeatureSelector< fromView.State>('view');
export const selectMediaState = createSelector(selectViewState, state => state.media);
export const selectActiveMediaQueries = createSelector(
  selectMediaState,
  fromMedia.selectActiveMediaQueries,
);
export const selectMediaQueryIsActive = createSelector(
  selectActiveMediaQueries,
  (activeMediaQueries: string[], props: { mediaQuery: string }) => {
    if (!activeMediaQueries) {
      return false;
    }

    return activeMediaQueries.includes(props.mediaQuery);
  },
);

export const selectAssignmentById = (id: number) => createSelector(
  assignmentSelectors.selectEntityMap,
  entityMap => entityMap?.[id]
);

export const selectAssignmentsByInterval = (props: {start: DateTime; end: DateTime }) => createSelector(
  assignmentSelectors.selectEntities,
  (assignments: Assignment[]) => {
    const assignmentsWithinDay: Assignment[] = [];
    for (const assignment of assignments) {
      if (props.start <= DateTime.fromISO(assignment.interval?.end) &&
          props.end >= DateTime.fromISO(assignment.interval?.start)
          ) {
            assignmentsWithinDay.push(assignment);
          }
    }
    return assignmentsWithinDay;
  }
);

export const selectAssignmentsByIntervalInclusive = (props: {start: DateTime; end: DateTime }) => createSelector(
  assignmentSelectors.selectEntities,
  (assignments: Assignment[]) => {
    const assignmentsWhichAreAtLeastPartlyContainedWithinInterval: Assignment[] = [];
    for (const assignment of assignments) {
      const assignmentStart = DateTime.fromISO(assignment.interval?.start);
      const assignmentEnd = DateTime.fromISO(assignment.interval?.end);
      const assignmentStartBetweenStartAndEnd = props.start <= assignmentStart && assignmentStart <= props.end;
      const assignmentEndBetweenStartAndEnd = props.start <= assignmentEnd && assignmentEnd <= props.end;

      if (assignmentStartBetweenStartAndEnd || assignmentEndBetweenStartAndEnd) {
        assignmentsWhichAreAtLeastPartlyContainedWithinInterval.push(assignment);
      }
    }
    return assignmentsWhichAreAtLeastPartlyContainedWithinInterval;
  }
);

export const selectAssignmentsForLast3MonthsForBatchReportList = createSelector(
  selectAssignmentsByIntervalInclusive({start: DateTime.local().minus({month: 3}), end: DateTime.local()}),
  truckSelectors.selectEntityMap,
  employeeSelectors.selectEntityMap,
  jobSelectors.selectEntityMap,
  customerSelectors.selectEntityMap,
  projectSelectors.selectEntityMap,
  (assignments, truckEntities, employeeEntities, jobEntities, customerEntities, projectEntities) => {
    return assignments.map((a) => {
      const truck: Truck = truckEntities[a.truckId];
      const driver: Employee = employeeEntities[a.driverId] ?? employeeEntities[truck?.defaultDriverId];
      const job = jobEntities[a.jobId];
      const customer = job ? customerEntities[job?.customerId] : null;
      const project = job?.projectId != null ? projectEntities[job.projectId] : null;
      return {
        id: a.id,
        truckId: a.truckId,
        truckName: truck?.name ?? 'Unknown',
        driverName: driver ? `${driver.givenNames} ${driver.surname}` : 'Unknown',
        customerName: customer?.name ?? 'Unknown',
        jobId: job?.id,
        jobName: job?.name ?? 'None',
        projectId: project?.id,
        projectName: project?.name ?? 'None',
        end: a.interval.end,
      };
    });
  }
);

// Multiple state compositions
export const selectAssignmentsWithDetails = createSelector(
  assignmentSelectors.selectEntities,
  customerSelectors.selectEntityMap,
  projectSelectors.selectEntityMap,
  jobSelectors.selectEntityMap,
  employeeSelectors.selectEntityMap,
  truckSelectors.selectEntityMap,
  assignmentTargetSelectors.selectEntityMap,
  (
    assignments,
    customerEntities,
    projectEntities,
    jobEntities,
    employeeEntities,
    truckEntities,
    assignmentTargetEntities,
  ) => {
    const newAssignments: Assignment[] = [];

    assignments.forEach((assignment) => {
      const truck = truckEntities[assignment.truckId];
      const job = jobEntities[assignment.jobId];
      const project = job ? projectEntities[job.projectId] : null;
      const jobTargetName = job ? job.name : assignment.targetId != null ? assignmentTargetEntities[assignment.targetId]?.name : null;
      const driver = assignment.driverId
          ? employeeEntities[assignment.driverId]
          : truck
            ? employeeEntities[truck.defaultDriverId]
            : null;
      const assignmentWithDetails = {
        ...assignment,
        details: {
          customer: job ? customerEntities[job.customerId] : null,
          project,
          job,
          truck: truckEntities[assignment.truckId],
          driver,
        },
        startDate: assignment.interval?.start,
        customerName: job ? customerEntities[job.customerId]?.name : null,
        jobTargetName,
        truckName: truck?.name ?? null,
        driverLastname: driver?.surname ?? null,
      };
      newAssignments.push(assignmentWithDetails);
    });

    return newAssignments;
  },
);
export const selectAssignmentEntitiesWithDetails = createSelector(
  assignmentSelectors.selectEntities,
  customerSelectors.selectEntityMap,
  projectSelectors.selectEntityMap,
  jobSelectors.selectEntityMap,
  employeeSelectors.selectEntityMap,
  truckSelectors.selectEntityMap,
  (
    assignments,
    customerEntities,
    projectEntities,
    jobEntities,
    employeeEntities,
    truckEntities,
  ) => {
    const newAssignmentEntities: { [id: number]: Assignment } = {};

    assignments.forEach((assignment) => {
      const truck = truckEntities[assignment.truckId];
      const job = jobEntities[assignment.jobId];
      const project = job ? projectEntities[job.projectId] : null;
      newAssignmentEntities[assignment.id] = {
        ...assignment,
        details: {
          customer: project ? customerEntities[project.customerId] : null,
          project,
          job,
          truck: truckEntities[assignment.truckId],
          driver: assignment.driverId
            ? employeeEntities[assignment.driverId]
            : truck
            ? employeeEntities[truck.defaultDriverId]
            : null,
        },
      };
    });

    return newAssignmentEntities;
  },
);
export const selectAssignmentWithDetailsForSelectedBatchReport = createSelector(
  selectAssignmentEntitiesWithDetails,
  selectSelectedEntity<BatchReport>('BatchReport'),
  (assignmentEntities, batchReport) => {
    if (!assignmentEntities || !batchReport) {
      return null;
    }
    return assignmentEntities[batchReport.assignmentId];
  },
);
export const selectAssignmentEntitiesWithDetailsForSelectedJob = createSelector(
  selectAssignmentEntitiesWithDetails,
  selectSelectedJob,
  (assignmentEntities, selectedJob) => {
    if (!assignmentEntities || !selectedJob || !selectedJob.assignmentIds) {
      return null;
    }

    const assignmentEntitiesForSelectedJob: { [id: number]: Assignment } = {};
    selectedJob.assignmentIds.forEach((assignmentId) => {
      if (assignmentEntities[assignmentId] != null) {
        assignmentEntitiesForSelectedJob[assignmentId] = assignmentEntities[assignmentId];
      }
    });
    return assignmentEntitiesForSelectedJob;
  },
);
export const selectBatchReportsBySelectedTruck = createSelector(
  selectSelectedTruckId,
  batchReportSelectors.selectEntities,
  (selectedTruckId, batchReports) => {
    if (selectedTruckId != null && batchReports != null) {
      return batchReports.filter(br => br.truckId === selectedTruckId);
    }
    return [];
  }
);
export const selectBatchReportsForSameJobAsSelectedBatchReport = createSelector(
  batchReportSelectors.selectEntityMap,
  selectBatchReportIdsByJobIds,
  selectAssignmentWithDetailsForSelectedBatchReport,
  (batchReportEntities, batchReportIdsByJobIds, assignment) => {
    if (
      !batchReportEntities ||
      !batchReportIdsByJobIds ||
      !assignment ||
      batchReportIdsByJobIds[assignment.jobId] == null
    ) {
      return null;
    }
    const batchReports: BatchReport[] = [];
    batchReportIdsByJobIds[assignment.jobId].forEach(id =>
      batchReports.push(batchReportEntities[id]),
    );
    return batchReports;
  },
);

export const selectContactsByCustomerId = (customerId: number) =>
  createSelector(
    contactSelectors.selectEntities,
    (contacts) => {
      return contacts?.filter(contact => contact.customerId === customerId) ?? [];
    },
  );

export const selectContactsForSelectedCustomer = createSelector(
  contactSelectors.selectEntities,
  selectSelectedCustomer,
  (contacts, customer) => {
    return contacts?.filter(contact => contact.customerId === customer?.id) ?? [];
  },
);

export const selectMixDesignForSelectedBatchReport = createSelector(
  mixDesignSelectors.selectEntityMap,
  selectSelectedBatchReport,
  (mixDesignEntities, selectedBatchReport) => {
    if (!mixDesignEntities || !selectedBatchReport || !selectedBatchReport.mixDesignSnapshot) {
      return null;
    }

    // If a mix design ID isn't present, indicate so instead of returning undefined.
    if (!mixDesignEntities[selectedBatchReport.mixDesignSnapshot.mixDesignId] == null) {
      return null;
    }

    return mixDesignEntities[selectedBatchReport.mixDesignSnapshot.mixDesignId];
  },
);
export const selectProjectsBySelectedCustomer = createSelector(
  projectSelectors.selectEntities,
  selectSelectedCustomerId,
  (projects, selectedCustomerId) =>
    projects?.filter(project => project.customerId === selectedCustomerId),
);
export const selectProjectsWithCustomer = createSelector(
  projectSelectors.selectEntities,
  customerSelectors.selectEntityMap,
  (projects, customers) => {
    const newProjects: Project[] = [];
    projects.forEach((p) => {
      newProjects.push({ ...p, customer: customers[p.customerId] } as Project);
    });
    return newProjects;
  },
);
export const selectProjectsWithCustomerByCustomerId = createSelector(
  projectSelectors.selectEntities,
  customerSelectors.selectEntityMap,
  (projects, customers, customerId: number) => {
    const newProjects: Project[] = [];
    projects
      .filter(p => p.customerId === customerId)
      .forEach((p) => {
        newProjects.push({ ...p, customer: customers[p.customerId] } as Project);
      });
    return newProjects;
  },
);
export const selectProjectByIdWithCustomer = createSelector(
  selectProjectById,
  customerSelectors.selectEntityMap,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  (project, customers, { projectId: number }) => {
    if (project == null) {
      return null;
    }
    return {
      ...project,
      customer: customers[project.customerId],
    };
  },
);
export const selectProjectBySelectedJob = createSelector(
  projectSelectors.selectEntityMap,
  selectSelectedJob,
  (projects, job) => {
    if (projects && job && projects[job.projectId]) {
      return projects[job.projectId];
    }

    return null;
  },
);
export const selectCustomerBySelectedJob = createSelector(
  customerSelectors.selectEntityMap,
  selectProjectBySelectedJob,
  (customers, project) => {
    if (customers && project && customers[project.customerId]) {
      return customers[project.customerId];
    }

    return null;
  },
);
export const selectCustomerBySelectedProject = createSelector(
  customerSelectors.selectEntityMap,
  selectSelectedProject,
  (customerEntities, project) => {
    if (customerEntities && project && customerEntities[project.customerId]) {
      return customerEntities[project.customerId];
    }

    return null;
  },
);
export const selectCustomerIdOfSelectedProjectAsArgs = createSelector(
  selectSelectedProject,
  (project) => {
    if (!project) {
      return null;
    }
    return { id: project.customerId };
  },
);
export const selectCustomerLoadedBySelectedProject = createSelector(
  customerSelectors.selectEntityMap,
  selectSelectedProject,
  (customerEntities, project) => {
    if (!customerEntities || !project) {
      return false; // Is there a different way we should handle
    }

    if (customerEntities[project.customerId] != null) {
      return true;
    }

    return false;
  },
);

export const selectTrucksWithDefaultDriver = createSelector(
  truckSelectors.selectEntities,
  employeeSelectors.selectEntityMap,
  (trucks: Truck[], employees: Dictionary<Employee>) => {
    const newTrucks: Truck[] = [];
    trucks.forEach((t) => {
      newTrucks.push({ ...t, defaultDriver: employees[t.defaultDriverId] } as Truck);
    });

    // console.log('hello!', newTrucks);
    return newTrucks;
  },
);

export const selectActiveTrucks = createSelector(
  truckSelectors.selectEntities,
  (trucks: Truck[]) => {
    return trucks.filter(t => !t.inactive);
  }
);

export const selectLoggerState = createFeatureSelector<fromLogger.State>('logger');
export const selectLoggerMessageQueue = createSelector(
  selectLoggerState,
  fromLogger.selectMessageQueue,
);
export const selectLoggerLevel = createSelector(selectLoggerState, fromLogger.selectLevel);

export const selectAuthState = createFeatureSelector<fromAuth.AuthState>('auth');

export const selectAuthStatus = createSelector(
  selectAuthState,
  (state: fromAuth.AuthState) => state.status,
);

export const selectSendingPing = createSelector(selectAuthStatus, fromAuthStatus.getSendingPing);
export const selectLastPingRtts = createSelector(selectAuthStatus, fromAuthStatus.getLastPingRtts);
export const selectPostLoginRedirectUrl = createSelector(
  selectAuthStatus,
  fromAuthStatus.getPostLoginRedirectUrl,
);
/*
export const selectUser = createSelector(selectAuthStatus, fromAuthStatus.getUser);
export const selectLoggedIn = createSelector(selectAuthStatus, fromAuthStatus.getLoggedIn);
export const selectLoginPingCompleted = createSelector(
  selectAuthStatus,
  fromAuthStatus.getLoginPingCompleted
);
export const selectPasswordResetTokenStatus = createSelector(selectAuthStatus, fromAuthStatus.getUsernameByResetToken);
export const selectPasswordResetSuccessful = createSelector(selectAuthStatus, (state: fromAuthStatus.State) => state.passwordResetSuccessful);
export const selectPasswordResetError = createSelector(selectAuthStatus, (state: fromAuthStatus.State) => state.errors.RESET_PASSWORD);
export const selectGetUsernameByResetTokenError = createSelector(selectAuthStatus, (state: fromAuthStatus.State) => state.errors.GET_PASSWORD_RESET_TOKEN_STATUS);
*/
export const selectMyCompany = createSelector(
  companySelectors.selectEntities,

  (companyEntities: Company[]) => {
    if (!companyEntities || companyEntities.length === 0) {
      return null;
    }

    return companyEntities[0];
  },
);
export const selectEmployeeByCurrentUser = createSelector(
  selectCurrentUser,
  employeeSelectors.selectEntities,
  (currentUser, employees) =>
    currentUser && employees?.find(employee => employee.userSubject === currentUser.sub),
);
export const selectCurrentUserCompanyId = createSelector(selectCurrentUser, user => user?.tenantId);

export const selectJobsByCustomerId = createSelector(
  jobSelectors.selectEntities,
  selectProjectsWithCustomerByCustomerId,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  (jobs: Job[], projects: Project[], customerId: number) =>
    jobs &&
    projects &&
    jobs.filter(job => projects.map(project => project.id).includes(job.projectId)),
);

export const selectJobsByInterval = (props: {start: DateTime; end: DateTime }) => createSelector(
  jobSelectors.selectEntities,
  (jobs: Job[]) => {
    const jobsWithinDay: Job[] = [];
    for (const job of jobs) {
      if (props.start <= DateTime.fromISO(job.scheduledInterval?.end) &&
          props.end >= DateTime.fromISO(job.scheduledInterval?.start)
          ) {
            jobsWithinDay.push(job);
          }
    }
    return jobsWithinDay;
  }
);

export const selectJobsByIntervalInclusive = (props: {start: DateTime; end: DateTime }) => createSelector(
  jobSelectors.selectEntities,
  (jobs: Job[]) => {
    const jobsWhichAreAtLeastPartlyContainedWithinInterval: Job[] = [];
    for (const job of jobs) {
      const jobStart = DateTime.fromISO(job.scheduledInterval?.start);
      const jobEnd = DateTime.fromISO(job.scheduledInterval?.end);
      const jobStartBetweenStartAndEnd = props.start <= jobStart && jobStart <= props.end;
      const jobEndBetweenStartAndEnd = props.start <= jobEnd && jobEnd <= props.end;

      if (jobStartBetweenStartAndEnd || jobEndBetweenStartAndEnd) {
        jobsWhichAreAtLeastPartlyContainedWithinInterval.push(job);
      }
    }
    return jobsWhichAreAtLeastPartlyContainedWithinInterval;
  }
);

export const selectJobsByCustomerIdAndProjectId = (customerId: number, projectId: number) =>
  createSelector(
    jobSelectors.selectEntities,
    (jobEntities) => {
      const jobs: Job[] = [];

      if (!jobEntities) return null;

      for (const job of jobEntities) {
        if ((customerId != null && job.customerId === customerId) || (projectId != null && job.projectId === projectId)) {
          jobs.push(job);
        }
      }
      return jobs;
    }
  );

export const selectNextJobTodayByTruckIdAndStart = (truckId: number, start: DateTime) =>
  createSelector(
    selectJobsByIntervalInclusive({ start, end: start.endOf('day') }),
    selectAssignmentsByIntervalInclusive({ start, end: start.endOf('day') }),
    (jobs, assignments) => {
      const assignmentsForThisTruck = assignments.filter(a => a.truckId === truckId);
      // sort assignments by start date (earliest to latest)
      assignmentsForThisTruck.sort((a, b) => {
        const dateA = new Date(a.interval?.start).getTime();
        const dateB = new Date(b.interval?.start).getTime();
        return dateA > dateB ? 1 : dateA < dateB ? -1 : 0;
      });
      const earliestAssignmentForThisTruck = assignmentsForThisTruck?.[0];
      return jobs.find(j => j.id === earliestAssignmentForThisTruck?.jobId) ?? null;
    }
  );

export const selectTrucksWithDefaultDriverAndNextJob = createSelector(
  truckSelectors.selectEntities,
  employeeSelectors.selectEntityMap,
  selectJobsByIntervalInclusive({ start: DateTime.local(), end: DateTime.local().endOf('day') }),
  selectAssignmentsByIntervalInclusive({ start: DateTime.local(), end: DateTime.local().endOf('day') }),
  (trucks, employees, jobs, assignments) => {
    const newTrucks = [];
    trucks.forEach((t) => {
      const assignmentsForThisTruck = assignments.filter(a => a.truckId === t.id);
      // sort assignments by start date (earliest to latest)
      assignmentsForThisTruck.sort((a, b) => {
        const dateA = new Date(a.interval?.start).getTime();
        const dateB = new Date(b.interval?.start).getTime();
        return dateA > dateB ? 1 : dateA < dateB ? -1 : 0;
      });
      const earliestAssignmentForThisTruck = assignmentsForThisTruck?.[0];
      const nextJob = jobs.find(j => j.id === earliestAssignmentForThisTruck?.jobId) ?? null;
      newTrucks.push({ ...t, defaultDriver: employees[t.defaultDriverId], nextJob } as Truck);
    });

    return newTrucks;
  }
);

export const selectUnitBySettingPath = (path: string[]) => createSelector(
  SettingsSelectors.selectResolvedSettingByPath(path),
  unitSelectors.selectEntityMap,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  (settingValue, unitEntities) => {
    if (!settingValue || !unitEntities) {
      return null;
    }

    return unitEntities[settingValue.value as number];
  },
);

export const selectMixDesignSlotName = createSelector(selectRouterRouteParams, (paramsArray) => {
  if (!paramsArray) {
    return '';
  }

  for (let i = paramsArray.length - 1; i >= 0; i--) {
    const slot = paramsArray[i].slot;
    if (slot) {
      if ('active' === slot) {
        return 'Active';
      }

      return `Slot ${slot}`;
    }
  }

  return '';
});

export const selectEmailAddressesForCustomerId = (customerId: number) => createSelector(
  customerSelectors.selectEntityMap,
  contactSelectors.selectEntityMap,
  (customerEntities, contactEntities) => {
    const emails = [];

    const customer = customerEntities[customerId];
    emails.push(customer?.mail ? `${customer.name} <${customer.mail}>` : null);

    for (const contactId of customer.contactIds) {
      const contact = contactEntities[contactId];
      emails.push(contact?.mail ? `${contact?.givenNames ? contact.givenNames + ' ' : ''}${contact?.surname ?? ''} <${contact.mail}>` : null);
    }
    const unique = emails.filter(function(item, pos) {
      return emails.indexOf(item) === pos;
    });

    return unique.filter(e => e != null && e !== '');
  }
);

export { State as RootState };
export { AuthSelectors };

